import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import {
  Form as BootstrapForm,
  Button,
  Card,
  Col,
  Row,
  Spinner,
} from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import { toast, ToastContainer } from "react-toastify";
import * as yup from "yup";
import franchiseAdminAPI from "../../api/franchiseAdminAPI";
import otpapi from "../../api/otpapi";
import TextField from "../../components/sessions/TextField";
import countries from "../../components/utils/countries";

// Single component that renders:
//  - the TOKEN / INVITE flow (decoded from ?token=... , sent by master admin)
//    -> OTP verify -> set password + country -> login
//  - the DIRECT /franchise-signin flow (no token)
//    -> normal email/password login, with a "Set Password" self-service
//       OTP + country + password flow
const FranchiseLogin = () => {
  const navigate = useNavigate();
  const locationHook = useLocation();
  const query = new URLSearchParams(locationHook.search);
  const token = query.get("token");
  const hasToken = Boolean(token);

  // ---------------- TOKEN (INVITE) FLOW STATE ----------------
  const [email, setEmail] = useState("");
  const [id, setId] = useState(0);
  const [organization, setOrganization] = useState("");
  const [locationName, setLocationName] = useState("");
  const [parentOrg, setParentOrg] = useState("");
  const [parentLoc, setParentLoc] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [tokenLoading, setTokenLoading] = useState(true);

  const [step, setStep] = useState("otp"); // otp -> password -> login

  const [sentOtp, setSentOtp] = useState("");
  const [enteredOtp, setEnteredOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [timerActive, setTimerActive] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [rePassword, setRePassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordMatchError, setPasswordMatchError] = useState("");

  const [country, setCountry] = useState(null);
  const [loginPassword, setLoginPassword] = useState("");

  // ---------------- DIRECT (NO TOKEN) FLOW STATE ----------------
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [directOtpSent, setDirectOtpSent] = useState(false);
  const [directSentOtp, setDirectSentOtp] = useState("");
  const [directEnteredOtp, setDirectEnteredOtp] = useState("");
  const [directOtpVerified, setDirectOtpVerified] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [directCountry, setDirectCountry] = useState(null);
  const [directShowPassword, setDirectShowPassword] = useState(false);
  const [directShowConfirmPassword, setDirectShowConfirmPassword] =
    useState(false);

  // Decode token (invite flow only)
  useEffect(() => {
    if (!hasToken) {
      setTokenLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decoded.exp && decoded.exp < currentTime) {
        setTokenError("Sorry, the link has expired.");
      } else {
        setEmail(decoded.email);
        setId(decoded.id);
        setOrganization(decoded.organization);
        setLocationName(decoded.location);
        setParentOrg(decoded.parentOrganization);
        setParentLoc(decoded.parentLocation);
      }
    } catch {
      setTokenError("Invalid or corrupted token.");
    } finally {
      setTokenLoading(false);
    }
  }, [hasToken, token]);

  // OTP countdown timer (invite flow)
  useEffect(() => {
    let interval;
    if (timerActive && countdown > 0) {
      interval = setInterval(() => setCountdown((p) => p - 1), 1000);
    } else if (countdown === 0) {
      setTimerActive(false);
    }
    return () => clearInterval(interval);
  }, [timerActive, countdown]);

  // ---------------- SHARED HELPERS ----------------
  const setAuthCookiesAndRedirect = (userEmail, isParent, countryValue) => {
    const isLocalhost = window.location.hostname === "localhost";
    const cookieOptions = {
      expires: 7,
      path: "/",
      ...(isLocalhost
        ? { secure: false, sameSite: "Lax" }
        : { secure: true, sameSite: "None", domain: ".signbook.co" }),
    };

    Cookies.set("userEmail", userEmail, cookieOptions);
    Cookies.set("isParent", String(isParent), cookieOptions);
    Cookies.set("country", countryValue || "", cookieOptions);

    navigate("/franchise-dashboard", { state: { email: userEmail } });
  };

  // ---------------- INVITE FLOW HANDLERS ----------------
  const handleSendOtp = async () => {
    setIsSendingOtp(true);
    try {
      const response = await otpapi.validateUserEmail(email);
      setSentOtp(response.data);
      setOtpSent(true);
      toast.success("OTP sent!");
      setCountdown(60);
      setTimerActive(true);
    } catch {
      toast.error("Failed to send OTP");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = () => {
    if (!otpSent || countdown === 0) {
      toast.error("OTP expired");
      return;
    }
    if (enteredOtp == sentOtp) {
      setOtpVerified(true);
      toast.success("OTP Verified");
    } else {
      toast.error("Invalid OTP");
    }
  };

  const validatePassword = (pwd) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;
    if (!regex.test(pwd)) {
      return "Min 8 chars, include upper, lower, number & special char";
    }
    return "";
  };

  const handleSetPassword = async () => {
    if (!country) {
      toast.error("Please select country");
      return;
    }

    try {
      await franchiseAdminAPI.setFranchisePassword({
        id,
        password,
      });

      await franchiseAdminAPI.setFranchiseCountry({
        id,
        country: country.value,
      });

      toast.success("Password set successfully!");

      setPassword("");
      setRePassword("");
      setPasswordError("");
      setPasswordMatchError("");
      setCountry(null);

      setStep("login");
    } catch {
      toast.error("Failed to set password");
    }
  };

  const handleInviteLogin = async () => {
    try {
      const response = await franchiseAdminAPI.loginFranchise({
        email,
        password: loginPassword,
      });

      const userEmail = response.data?.email;
      const isParent = response.data?.isParent;
      if (!userEmail) throw new Error("Invalid API response");

      toast.success("Login successful!");
      setAuthCookiesAndRedirect(userEmail, isParent, response.data?.country);
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid email or password");
    }
  };

  // ---------------- DIRECT LOGIN HANDLERS ----------------
  const validationSchema = yup.object().shape({
    userEmail: yup
      .string()
      .email("Invalid email")
      .required("Email is required"),
    password: yup
      .string()
      .min(8, "Password must be 8 characters long")
      .required("Password is required"),
  });

  const handleLogin = async (values, setSubmitting) => {
    try {
      const response = await franchiseAdminAPI.loginFranchise({
        email: values.userEmail,
        password: values.password,
      });

      const userEmail = response.data?.email;
      const isParent = response.data?.isParent;
      if (!userEmail) throw new Error("Invalid API response");

      toast.success("Login successful!");
      setAuthCookiesAndRedirect(userEmail, isParent, response.data?.country);
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "Login failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------- DIRECT SELF-SERVICE SET PASSWORD HANDLERS ----------------
  const handleDirectSendOtp = async () => {
    if (!resetEmail) {
      toast.error("Please enter email");
      return;
    }
    setSendingOtp(true);
    try {
      const response = await otpapi.validateUserEmail(resetEmail);
      setDirectSentOtp(response.data);
      setDirectOtpSent(true);
      toast.success("OTP sent successfully!");
    } catch {
      toast.error("Failed to send OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleDirectVerifyOtp = async () => {
    setVerifyingOtp(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (directEnteredOtp == directSentOtp) {
        toast.success("OTP verified successfully!");
        setDirectOtpVerified(true);
      } else {
        toast.error("Invalid OTP");
      }
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setUpdatingPassword(true);
    try {
      await franchiseAdminAPI.setFranchisePasswordByEmail({
        email: resetEmail,
        password: newPassword,
        country: directCountry?.value,
      });

      toast.success("Password created successfully!");

      setTimeout(() => {
        setShowSetPassword(false);
        setDirectOtpSent(false);
        setDirectOtpVerified(false);
        setResetEmail("");
        setDirectEnteredOtp("");
        setNewPassword("");
        setConfirmPassword("");
        setDirectCountry(null);
      }, 2000);
    } catch {
      toast.error("Failed to update password");
    } finally {
      setTimeout(() => setUpdatingPassword(false), 2000);
    }
  };

  // ---------------- RENDER ----------------

  if (hasToken && tokenLoading) {
    return null;
  }

  if (hasToken && tokenError) {
    return (
      <div className="auth-layout-wrap">
        <div className="auth-content">
          <Card className="o-hidden">
            <div className="p-4 text-center">
              <h5 style={{ color: "red" }}>{tokenError}</h5>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-layout-wrap">
      <div className="auth-content">
        <Card className="o-hidden card">
          <Row>
            <Col md={6} className="text-center auth-cover">
              <div className="ps-3 auth-right p-4">
                <div className="auth-logo text-center mt-4">
                  <img src="/assets/images/logo.png" alt="E-signature" />
                  <h3 style={{ color: "black" }}>E-Signature</h3>
                </div>
                {!hasToken && (
                  <>
                    <h1 className="mb-3 text-18 mt-4">Welcome Back!</h1>
                    <p className="mb-4">Please sign in to your account</p>
                  </>
                )}
              </div>
            </Col>

            <Col md={6}>
              <div className="p-4">
                {hasToken ? (
                  <>
                    <h5>
                      <strong>{organization}</strong> added for{" "}
                      <strong>{locationName}</strong> managed by{" "}
                      <strong>{email}</strong>
                    </h5>

                    <BootstrapForm>
                      {step === "otp" && (
                        <>
                          <BootstrapForm.Control
                            type="tel"
                            maxLength={6}
                            placeholder="Enter OTP"
                            value={enteredOtp}
                            onChange={(e) =>
                              setEnteredOtp(e.target.value.replace(/\D/g, ""))
                            }
                            className="mb-3"
                          />

                          <Row className="mb-3">
                            <Col xs={9}>
                              <Button
                                onClick={handleSendOtp}
                                disabled={isSendingOtp || timerActive}
                              >
                                {timerActive
                                  ? `Resend in ${countdown}s`
                                  : "Send OTP"}
                              </Button>
                            </Col>
                            <Col xs={3}>
                              <Button onClick={handleVerifyOtp}>Verify</Button>
                            </Col>
                          </Row>

                          <BootstrapForm.Check
                            type="checkbox"
                            label="Confirm"
                            checked={checkboxChecked}
                            disabled={!otpVerified}
                            onChange={(e) =>
                              setCheckboxChecked(e.target.checked)
                            }
                          />

                          <Button
                            className="mt-2"
                            disabled={!checkboxChecked}
                            onClick={() => setStep("password")}
                          >
                            Proceed
                          </Button>
                        </>
                      )}

                      {step === "password" && (
                        <>
                          <BootstrapForm.Group className="mb-3">
                            <BootstrapForm.Label>
                              Select Country
                            </BootstrapForm.Label>
                            <CreatableSelect
                              options={countries}
                              value={country}
                              onChange={(selectedOption) => {
                                if (selectedOption?.__isNew__) {
                                  setCountry({
                                    label: selectedOption.label,
                                    value: selectedOption.label
                                      .toUpperCase()
                                      .slice(0, 2),
                                  });
                                } else {
                                  setCountry(selectedOption);
                                }
                              }}
                              placeholder="Select your organization location"
                              isClearable
                            />
                          </BootstrapForm.Group>

                          <BootstrapForm.Group className="mb-3 position-relative">
                            <BootstrapForm.Label>Password</BootstrapForm.Label>
                            <BootstrapForm.Control
                              type={showPassword ? "text" : "password"}
                              placeholder="Password *"
                              value={password}
                              onChange={(e) => {
                                setPassword(e.target.value);
                                setPasswordError(
                                  validatePassword(e.target.value)
                                );
                              }}
                            />
                            <button
                              type="button"
                              className="btn position-absolute"
                              style={{ right: 10, top: "60%" }}
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                            {passwordError && (
                              <small style={{ color: "red" }}>
                                {passwordError}
                              </small>
                            )}
                          </BootstrapForm.Group>

                          <BootstrapForm.Group className="mb-3 position-relative">
                            <BootstrapForm.Label>
                              Confirm Password
                            </BootstrapForm.Label>
                            <BootstrapForm.Control
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder="Retype Password *"
                              value={rePassword}
                              onChange={(e) => {
                                setRePassword(e.target.value);
                                setPasswordMatchError(
                                  e.target.value !== password
                                    ? "Passwords do not match"
                                    : ""
                                );
                              }}
                            />
                            <button
                              type="button"
                              className="btn position-absolute"
                              style={{ right: 10, top: "60%" }}
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                            >
                              {showConfirmPassword ? (
                                <FaEyeSlash />
                              ) : (
                                <FaEye />
                              )}
                            </button>
                          </BootstrapForm.Group>

                          {passwordMatchError && (
                            <p style={{ color: "red" }}>
                              {passwordMatchError}
                            </p>
                          )}

                          <Button
                            variant="success"
                            disabled={
                              !password ||
                              !rePassword ||
                              passwordError ||
                              password !== rePassword ||
                              !country
                            }
                            onClick={handleSetPassword}
                          >
                            Submit
                          </Button>
                        </>
                      )}

                      {step === "login" && (
                        <>
                          <BootstrapForm.Group className="mb-3">
                            <BootstrapForm.Control
                              type="text"
                              value={email}
                              disabled
                            />
                          </BootstrapForm.Group>

                          <BootstrapForm.Group className="mb-3 position-relative">
                            <BootstrapForm.Control
                              type="password"
                              placeholder="Enter Password"
                              value={loginPassword}
                              onChange={(e) =>
                                setLoginPassword(e.target.value)
                              }
                            />
                          </BootstrapForm.Group>

                          <Button onClick={handleInviteLogin}>Login</Button>
                        </>
                      )}
                    </BootstrapForm>
                  </>
                ) : (
                  <>
                    {!showSetPassword && (
                      <Formik
                        initialValues={{ userEmail: "", password: "" }}
                        validationSchema={validationSchema}
                        onSubmit={(values, { setSubmitting }) =>
                          handleLogin(values, setSubmitting)
                        }
                      >
                        {({
                          values,
                          errors,
                          touched,
                          handleChange,
                          handleBlur,
                          handleSubmit,
                          isSubmitting,
                        }) => (
                          <Form onSubmit={handleSubmit}>
                            <TextField
                              type="email"
                              name="userEmail"
                              label="Email address"
                              value={values.userEmail}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              helperText={touched.userEmail && errors.userEmail}
                            />

                            <div className="position-relative">
                              <TextField
                                type={directShowPassword ? "text" : "password"}
                                name="password"
                                label="Password"
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                helperText={touched.password && errors.password}
                              />
                              <button
                                type="button"
                                className="btn btn-link position-absolute"
                                style={{
                                  right: "60px",
                                  top: "70%",
                                  transform: "translateY(-50%)",
                                }}
                                onClick={() =>
                                  setDirectShowPassword(!directShowPassword)
                                }
                              >
                                {directShowPassword ? (
                                  <FaEyeSlash size={18} />
                                ) : (
                                  <FaEye size={18} />
                                )}
                              </button>
                            </div>

                            <button
                              type="submit"
                              className="btn btn-primary w-100 mt-3"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? "Signing In..." : "Login"}
                            </button>

                            <div className="text-center mt-3">
                              <button
                                type="button"
                                className="btn btn-link"
                                onClick={() => {
                                  setShowSetPassword(true);
                                  setDirectOtpSent(false);
                                  setDirectOtpVerified(false);
                                  setDirectEnteredOtp("");
                                  setResetEmail("");
                                }}
                              >
                                Set Password
                              </button>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    )}

                    {showSetPassword && (
                      <div>
                        {!directOtpVerified && (
                          <>
                            <BootstrapForm.Group className="mb-3">
                              <BootstrapForm.Label>Email</BootstrapForm.Label>
                              <BootstrapForm.Control
                                type="email"
                                placeholder="Enter Email"
                                value={resetEmail}
                                disabled={directOtpSent}
                                onChange={(e) => setResetEmail(e.target.value)}
                              />
                            </BootstrapForm.Group>

                            {!directOtpSent && (
                              <Button
                                variant="primary"
                                className="mb-3"
                                onClick={handleDirectSendOtp}
                                disabled={sendingOtp}
                              >
                                {sendingOtp ? (
                                  <>
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      className="me-2"
                                    />
                                    Sending...
                                  </>
                                ) : (
                                  "Send OTP"
                                )}
                              </Button>
                            )}

                            {directOtpSent && (
                              <>
                                <BootstrapForm.Group className="mb-3">
                                  <BootstrapForm.Label>
                                    Enter OTP
                                  </BootstrapForm.Label>
                                  <BootstrapForm.Control
                                    type="text"
                                    placeholder="Enter OTP"
                                    value={directEnteredOtp}
                                    onChange={(e) =>
                                      setDirectEnteredOtp(e.target.value)
                                    }
                                  />
                                </BootstrapForm.Group>

                                <Button
                                  variant="success"
                                  onClick={handleDirectVerifyOtp}
                                  disabled={verifyingOtp}
                                >
                                  {verifyingOtp ? (
                                    <>
                                      <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                      />
                                      Verifying...
                                    </>
                                  ) : (
                                    "Verify OTP"
                                  )}
                                </Button>
                              </>
                            )}
                          </>
                        )}

                        {directOtpVerified && (
                          <>
                            <BootstrapForm.Group className="mb-3">
                              <BootstrapForm.Label>
                                Select Country
                              </BootstrapForm.Label>
                              <CreatableSelect
                                options={countries}
                                value={directCountry}
                                onChange={(selectedOption) => {
                                  if (selectedOption?.__isNew__) {
                                    setDirectCountry({
                                      label: selectedOption.label,
                                      value: selectedOption.label
                                        .toUpperCase()
                                        .slice(0, 2),
                                    });
                                  } else {
                                    setDirectCountry(selectedOption);
                                  }
                                }}
                                placeholder="Select your country"
                                isClearable
                              />
                            </BootstrapForm.Group>

                            <BootstrapForm.Group className="mb-3 position-relative">
                              <BootstrapForm.Label>
                                New Password
                              </BootstrapForm.Label>
                              <BootstrapForm.Control
                                type={directShowPassword ? "text" : "password"}
                                placeholder="New Password"
                                value={newPassword}
                                onChange={(e) =>
                                  setNewPassword(e.target.value)
                                }
                              />
                              <button
                                type="button"
                                className="btn position-absolute"
                                style={{ right: 10, top: "60%" }}
                                onClick={() =>
                                  setDirectShowPassword(!directShowPassword)
                                }
                              >
                                {directShowPassword ? (
                                  <FaEyeSlash />
                                ) : (
                                  <FaEye />
                                )}
                              </button>
                            </BootstrapForm.Group>

                            <BootstrapForm.Group className="mb-3 position-relative">
                              <BootstrapForm.Label>
                                Confirm Password
                              </BootstrapForm.Label>
                              <BootstrapForm.Control
                                type={
                                  directShowConfirmPassword
                                    ? "text"
                                    : "password"
                                }
                                placeholder="Confirm Password"
                                value={confirmPassword}
                                onChange={(e) =>
                                  setConfirmPassword(e.target.value)
                                }
                              />
                              <button
                                type="button"
                                className="btn position-absolute"
                                style={{ right: 10, top: "60%" }}
                                onClick={() =>
                                  setDirectShowConfirmPassword(
                                    !directShowConfirmPassword
                                  )
                                }
                              >
                                {directShowConfirmPassword ? (
                                  <FaEyeSlash />
                                ) : (
                                  <FaEye />
                                )}
                              </button>
                            </BootstrapForm.Group>

                            <Button
                              variant="warning"
                              onClick={handleUpdatePassword}
                              disabled={updatingPassword || !directCountry}
                            >
                              {updatingPassword ? (
                                <>
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                  Updating...
                                </>
                              ) : (
                                "Update Password"
                              )}
                            </Button>
                          </>
                        )}

                        <div className="text-center mt-3">
                          <button
                            type="button"
                            className="btn btn-link"
                            onClick={() => setShowSetPassword(false)}
                          >
                            Back to Login
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Card>

        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </div>
  );
};

export default FranchiseLogin;